import React from 'react';

const LobsterAI = () => (
  <article className="news-article-body">
    <p className="news-lead">
      LobsterAI - internally called 养龙虾 (yǎng lóngxiā), literally "raising lobsters" -
      is an AI agent framework developed by NetEase Youdao. The name comes from an
      analogy used by the team: managing multiple AI agents is like running a lobster farm,
      where you need to keep track of many creatures that all need attention at different
      times. It's a more honest name for agent orchestration than most projects use.
    </p>

    <h2>What LobsterAI is built to do</h2>
    <p>
      LobsterAI is a multi-agent orchestration framework focused on educational and
      productivity applications. Key capabilities:
    </p>
    <ul>
      <li><strong>Task decomposition:</strong> Breaking complex user requests into subtasks assigned to specialised agents</li>
      <li><strong>Agent lifecycle management:</strong> Creating, monitoring, and terminating agents as tasks complete</li>
      <li><strong>Knowledge grounding:</strong> Connecting agents to external knowledge bases to reduce hallucination</li>
      <li><strong>Tool integration:</strong> Agents can call web search, code execution, and document analysis tools</li>
    </ul>
    <p>
      The educational angle is important. Youdao's core product is a language learning and
      tutoring platform. LobsterAI enables personalised AI tutors that can decompose a
      complex topic, generate exercises, check understanding, and adjust difficulty - all
      handled by coordinated specialised agents rather than one general-purpose model.
    </p>

    <h2>NetEase Youdao's position</h2>
    <p>
      NetEase Youdao is NetEase's education technology division, best known for the Youdao
      Dictionary app and online courses. They've been integrating AI aggressively since 2023,
      using a combination of in-house models and API access to frontier models.
    </p>
    <p>
      LobsterAI represents their internal infrastructure for all AI-powered features - rather
      than being primarily an external product, it's the platform their product teams build on.
      The public documentation emerged through developer conference presentations rather than
      a formal open-source release.
    </p>

    <h2>The "raising lobsters" metaphor</h2>
    <p>
      The name deserves more attention than it usually gets. The lobster farming analogy
      captures something real about multi-agent systems: like lobsters in a tank, AI agents
      need feeding (context), space (memory budget), and separation (clear task boundaries)
      or they'll start interfering with each other. The team found that agents without
      clear boundaries and resource limits tended to "fight" - consuming each other's context
      window allocations and producing incoherent outputs.
    </p>
    <p>
      It's a surprisingly honest metaphor from a team that clearly has operational experience
      with the failure modes of agent systems.
    </p>

    <h2>Influence on OpenClaw</h2>
    <p>
      LobsterAI's approach to agent lifecycle management influenced the design of OpenClaw
      (the Claude-based agent toolkit). Several OpenClaw contributors were familiar with
      Youdao's conference presentations and borrowed the explicit agent lifecycle concept -
      create, run, monitor, terminate - rather than the more common approach of just
      "prompt looping" until done.
    </p>

    <div className="news-insight-box">
      <h3>The big lesson</h3>
      <p>
        Multi-agent systems require real operational thinking - resource allocation, lifecycle
        management, clear task boundaries, failure handling. LobsterAI's framework shows that
        the teams doing this seriously are treating agent orchestration like distributed systems
        engineering, not just prompt engineering. That's the right approach, and it's why
        the "raising lobsters" metaphor stuck.
      </p>
    </div>
  </article>
);

export default LobsterAI;
